import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useDrop } from "react-dnd";
import { Button, Alert } from "react-bootstrap";
import { Save, Link, Unlink, Trash2 } from "lucide-react";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import DraggableComponent from "../components/algoStudio/DraggableComponent";
import Node from "../components/algoStudio/Node";
import ConfigurationPanel from "../components/algoStudio/ConfigurationPanel";
import { NodeConfig, CanvasState } from "../types/algoStudio";
import Canvas from "../components/algoStudio/Canvas";
import { useParams, useLocation } from "react-router-dom";

const ASSET_CLASS_COMPONENTS = [
  "Stocks",
  "Stock Options",
  "Futures",
  "Futures Options",
  "Forex",
  "Crypto Spot",
  "Crypto Futures",
];

// Styled components
const StudioContainer = styled.div`
  display: flex;
  height: calc(100vh - 64px);
  background: #090a0f;
`;

const LeftPanel = styled.div`
  width: 250px;
  background: #11131a;
  border-right: 1px solid #252a38;
  padding: 16px;
  overflow-y: auto;
`;

const RightPanel = styled.div`
  width: 800px;
  background: #11131a;
  border-left: 1px solid #252a38;
  padding: 16px;
  overflow-y: auto;
`;

const ComponentGroup = styled.div`
  margin-bottom: 24px;
`;

const ComponentTitle = styled.h6`
  font-weight: 600;
  margin-bottom: 12px;
  color: #e8eaed;
  font-size: 0.8125rem;
`;

const ComponentHint = styled.p`
  font-size: 0.75rem;
  color: #8b919a;
  margin-top: 8px;
`;

const CanvasControls = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 8px;
  z-index: 1000;
`;

const SaveButton = styled(Button)`
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 1000;
`;

const AlgoStudio: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [selectedNode, setSelectedNode] = useState<NodeConfig | null>(null);
  const [canvasState, setCanvasState] = useState<CanvasState>({
    nodes: [],
    connections: [],
  });
  const [currentAlgoId, setCurrentAlgoId] = useState<number | null>(
    id ? parseInt(id) : null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const activeAssetClassNode = useMemo(
    () =>
      canvasState.nodes.find((node) =>
        ASSET_CLASS_COMPONENTS.includes(node.type)
      ),
    [canvasState.nodes]
  );

  const activeAssetClass = activeAssetClassNode
    ? (activeAssetClassNode.type as string)
    : null;
  const hasAssetClassComponent = Boolean(activeAssetClass);

  // Load existing algo if editing
  useEffect(() => {
    const loadExistingAlgo = async () => {
      if (id) {
        try {
          const response = await fetch(`/api/algos/${id}`);
          if (response.ok) {
            const algo = await response.json();
            setCurrentAlgoId(algo.id);

            // Create nodes from algo parameters
            const nodes: NodeConfig[] = [];

            // Add Algorithm node
            if (algo.parameters.algorithm) {
              const algorithmConfig = {
                name: algo.name,
                description: algo.description,
                timeframe: algo.timeframe,
                ...algo.parameters.algorithm,
              };

              // Map indicators to movingAverages if present (backend uses 'indicators', frontend uses 'movingAverages')
              if (
                algorithmConfig.indicators &&
                (!algorithmConfig.movingAverages ||
                  algorithmConfig.movingAverages.length === 0)
              ) {
                algorithmConfig.movingAverages = algorithmConfig.indicators;
              }

              nodes.push({
                id: uuidv4(),
                type: "Algorithm",
                position: { x: 100, y: 100 },
                config: algorithmConfig,
              });
            }

            // Add Asset Class nodes
            if (algo.parameters.assetClasses) {
              algo.parameters.assetClasses.forEach((ac: any, index: number) => {
                nodes.push({
                  id: uuidv4(),
                  type: ac.type,
                  position: { x: 100, y: 200 + index * 100 },
                  config: ac.config,
                });
              });
            }

            // Add Symbol nodes
            if (algo.parameters.symbols) {
              algo.parameters.symbols.forEach((symbol: any, index: number) => {
                nodes.push({
                  id: uuidv4(),
                  type: "Symbol",
                  position: { x: 300, y: 200 + index * 100 },
                  config: symbol,
                });
              });
            }

            // Add Risk Management node
            if (algo.parameters.risk) {
              nodes.push({
                id: uuidv4(),
                type: "Trade Execution and Management",
                position: { x: 500, y: 100 },
                config: algo.parameters.risk,
              });
            }

            // Add Schedule node
            if (algo.parameters.schedule) {
              nodes.push({
                id: uuidv4(),
                type: "Schedule",
                position: { x: 500, y: 200 },
                config: algo.parameters.schedule,
              });
            }

            setCanvasState((prev) => ({
              ...prev,
              nodes,
            }));
          }
        } catch (error) {
          console.error("Error loading algo:", error);
        }
      }
    };

    loadExistingAlgo();
  }, [id]);

  // Function to create a new algo if none exists
  const ensureAlgoExists = useCallback(async () => {
    if (currentAlgoId) return currentAlgoId;

    try {
      const response = await fetch("/api/algos", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: "Untitled Algorithm",
          description: "",
          timeframe: "1h",
          parameters: {
            assetClasses: [],
            symbols: [],
            algorithm: {
              indicators: [],
              entryConditions: {},
              exitConditions: {},
            },
            risk: {},
            schedule: {},
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to create algorithm");
      }

      const newAlgo = await response.json();
      setCurrentAlgoId(newAlgo.id);
      return newAlgo.id;
    } catch (error) {
      console.error("Error creating algorithm:", error);
      throw error;
    }
  }, [currentAlgoId]);

  const getDefaultConfig = useCallback(
    (type: string): Record<string, any> => {
      switch (type) {
        case "Stocks":
          return {
            sharesPerTrade: 100,
            allowedTradeDirections: "Long Only",
          };
        case "Stock Options":
          return {
            optionType: "Call",
            optionMoneyness: "ATM",
            daysToExpiration: 30,
            strikeSelectionMethod: "Closest ATM",
            maxPricePerContract: 5.0,
            contractsPerTrade: 1,
          };
        case "Futures":
          return {
            allowedTradeDirections: "Long Only",
            contractsPerTrade: 1,
            contractSelection: "Front Month",
            enableAutomaticRollover: true,
            daysBeforeExpiryForRollover: 5,
            rolloverTime: "09:30",
          };
        case "Futures Options":
          return {
            optionType: "Call",
            optionMoneyness: "ATM",
            daysToExpiration: 30,
            strikeSelectionMethod: "Closest ATM",
            maxPricePerContract: 5.0,
            contractsPerTrade: 1,
            daysBeforeExpiryForRollover: 5,
            rolloverTime: "09:30",
          };
        case "Forex":
          return {
            allowedTradeDirections: "Long Only",
            lotSize: 0.01,
            leverage: "1:100",
            addSpreadToStopLoss: true,
            addSpreadToTakeProfit: true,
            riskSource: "Account Balance",
            fixedCapital: 1000,
            riskPercentage: 1.0,
            maxDollarRisk: 100,
            lotMode: "Fixed",
          };
        case "Crypto Spot":
          return {
            allowedTradeDirections: "Long Only",
            quantityPerTrade: 0.01,
            maxSpendingPerTrade: 1000,
            maxTotalSpendingPerDay: 5000,
          };
        case "Crypto Futures":
          return {
            allowedTradeDirections: "Long Only",
            quantityPerTrade: 0.01,
            maxSpendingPerTrade: 1000,
            maxTotalSpendingPerDay: 5000,
            contractType: "Perpetual Swap",
          };
        case "Symbol":
          return {
            symbol: "",
            assetClass: activeAssetClass || "Stocks",
            maxCapital: 10000,
            takeProfitValue: 0,
            stopLossValue: 0,
            maxRiskMode: "percentage",
            maxRiskValue: 0,
            lotSize: 1,
            limitOrderUpdateValue: 50,
          };
        case "Algorithm":
          return {
            name: "",
            description: "",
            timeframe: "1h",
            movingAverages: [],
            entryConditions: {
              buyConditions: [],
              sellConditions: [],
            },
            exitConditions: {
              buyExitConditions: [],
              sellExitConditions: [],
            },
          };
        case "Trade Execution and Management":
          return {
            takeProfitPercentage: 2.0,
            stopLossPercentage: 1.0,
            breakevenAfterProfit: 0,
            closeTradeAfterDrawdown: 0,
            maxPositionDuration: 0,
            trailingStopEnabled: false,
            trailingStopType: "percentage",
            trailingStopAmount: 1.0,
            trailingTakeProfitEnabled: false,
            trailingTakeProfitType: "percentage",
            trailingTakeProfitAmount: 1.0,
            orderType: "market",
            maxOrders: 10,
            allowPyramiding: false,
            allowHedging: false,
            closeOnBiasFlip: true,
            stackEntriesEveryBar: true,
            enterWhenFlat: false,
            maxPyramidPositions: 3,
            allowReversal: false,
            limitOrderUpdateInterval: 30,
            limitOrderUpdateValue: 100,
            useIntrabarCalculation: false,
            maxDrawdown: 1000,
            maxDrawdownPercentage: 10.0,
          };
        case "Schedule":
          return {
            activeShutdown: true,
            closeDay: "Friday",
            closeTime: "16:00",
            restartDay: "Monday",
            restartTime: "09:30",
            dailyStartTime: "09:30",
            dailyStopTime: "16:00",
            weeklyShutdown: true,
            marketOpenTime: "09:30",
            marketCloseTime: "16:00",
          };
        default:
          return {};
      }
    },
    [activeAssetClass]
  );

  const handleComponentSave = useCallback(
    async (node: NodeConfig) => {
      try {
        setIsSaving(true);
        const algoId = await ensureAlgoExists();

        let componentType: string;
        let componentData: any;

        switch (node.type) {
          case "Algorithm":
            componentType = "algorithm";
            componentData = {
              name: node.config.name || "Untitled Algorithm",
              description: node.config.description || "",
              timeframe: node.config.timeframe || "1h",
              movingAverages: node.config.movingAverages || [],
              entryConditions: {
                buyConditions: node.config.entryConditions?.buyConditions || [],
                sellConditions:
                  node.config.entryConditions?.sellConditions || [],
              },
              exitConditions: {
                buyExitConditions:
                  node.config.exitConditions?.buyExitConditions || [],
                sellExitConditions:
                  node.config.exitConditions?.sellExitConditions || [],
              },
            };
            break;
          case "Stocks":
          case "Stock Options":
          case "Futures":
          case "Futures Options":
          case "Forex":
          case "Crypto Spot":
          case "Crypto Futures":
            componentType = "assetClasses";
            componentData = [
              {
                type: node.type,
                config: node.config,
              },
            ];
            break;
          case "Symbol":
            componentType = "symbols";
            componentData = [
              {
                symbol: node.config.symbol,
                assetClass: activeAssetClass || node.config.assetClass,
                maxCapital: node.config.maxCapital,
                takeProfitValue: node.config.takeProfitValue || 0,
                stopLossValue: node.config.stopLossValue || 0,
                maxRiskMode: node.config.maxRiskMode || "percentage",
                maxRiskValue: node.config.maxRiskValue || 0,
                lotSize: node.config.lotSize || 1,
                limitOrderUpdateValue: node.config.limitOrderUpdateValue || 50,
              },
            ];
            break;
          case "Trade Execution and Management":
            componentType = "risk";
            componentData = node.config;
            break;
          case "Schedule":
            componentType = "schedule";
            componentData = node.config;
            break;
          default:
            throw new Error(`Unsupported component type: ${node.type}`);
        }

        const response = await fetch(
          `/api/algos/${algoId}/components/${componentType}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(componentData),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.detail || "Failed to save component configuration"
          );
        }

        const result = await response.json();
        console.log(
          `Successfully saved ${componentType} configuration:`,
          result
        );
        // TODO: Show success message to user
      } catch (error) {
        console.error("Error saving component configuration:", error);
        // TODO: Show error message to user
      } finally {
        setIsSaving(false);
      }
    },
    [ensureAlgoExists, activeAssetClass]
  );

  const handleDrop = useCallback(
    async (type: string, x: number, y: number) => {
      const allowedTypes = [
        ...ASSET_CLASS_COMPONENTS,
        "Symbol",
        "Algorithm",
        "Trade Execution and Management",
        "Schedule",
      ];

      if (!allowedTypes.includes(type)) {
        return;
      }

      const isAssetClassType = ASSET_CLASS_COMPONENTS.includes(type);

      if (!hasAssetClassComponent && !isAssetClassType) {
        console.warn(
          "Select an asset class before adding other components to the canvas."
        );
        return;
      }

      if (isAssetClassType && hasAssetClassComponent) {
        console.warn(
          "Only one asset class component can be active at a time. Delete the existing one to add another."
        );
        return;
      }

      const newNode: NodeConfig = {
        id: uuidv4(),
        type,
        position: { x, y },
        config: getDefaultConfig(type),
      };

      setCanvasState((prev) => ({
        ...prev,
        nodes: [...prev.nodes, newNode],
      }));

      // Save the new component's configuration immediately
      await handleComponentSave(newNode);
    },
    [handleComponentSave, hasAssetClassComponent, getDefaultConfig]
  );

  const handleConfigChange = useCallback(
    async (nodeId: string, newConfig: Record<string, any>) => {
      // Update the selected node state synchronously to ensure the panel reflects changes immediately
      if (selectedNode && selectedNode.id === nodeId) {
        setSelectedNode((prev) => (prev ? { ...prev, config: newConfig } : null));
      }

      // Update the node in the canvas state
      let updatedNode: NodeConfig | undefined;
      setCanvasState((prev) => {
        const updatedNodes = prev.nodes.map((node) => {
          if (node.id === nodeId) {
            updatedNode = { ...node, config: newConfig };
            return updatedNode;
          }
          return node;
        });
        return {
          ...prev,
          nodes: updatedNodes,
        };
      });

      // Find the node and save its configuration asynchronously
      if (updatedNode) {
        await handleComponentSave(updatedNode);
      }
    },
    [handleComponentSave, selectedNode]
  );

  const handleSaveComponent = useCallback(async () => {
    if (!selectedNode) return;

    try {
      setIsSaving(true);
      await handleComponentSave(selectedNode);
      // TODO: Show success message
    } catch (error) {
      console.error("Error saving component:", error);
      // TODO: Show error message to user
    } finally {
      setIsSaving(false);
    }
  }, [selectedNode, handleComponentSave]);

  // Add reset function
  const resetCanvas = useCallback(() => {
    setCanvasState({
      nodes: [],
      connections: [],
    });
    setSelectedNode(null);
    setCurrentAlgoId(null);
  }, []);

  // Update handleSaveAlgo to show success message and reset canvas
  const handleSaveAlgo = useCallback(async () => {
    try {
      setIsSaving(true);

      // Find the Algorithm component
      const algorithmNode = canvasState.nodes.find(
        (node) => node.type === "Algorithm"
      );
      if (!algorithmNode) {
        throw new Error("Algorithm component not found");
      }

      // Find Asset Class components
      const assetClassNodes = canvasState.nodes.filter((node) =>
        [
          "Stocks",
          "Stock Options",
          "Futures",
          "Futures Options",
          "Forex",
          "Crypto Spot",
          "Crypto Futures",
        ].includes(node.type)
      );

      // Find Symbol components
      const symbolNodes = canvasState.nodes.filter(
        (node) => node.type === "Symbol"
      );

      // Find Risk Management component
      const riskNode = canvasState.nodes.find(
        (node) => node.type === "Trade Execution and Management"
      );

      // Find Schedule component
      const scheduleNode = canvasState.nodes.find(
        (node) => node.type === "Schedule"
      );

      // Format the data according to the required structure
      const algoData = {
        name: algorithmNode.config.name || "Untitled Algorithm",
        description: algorithmNode.config.description || "",
        timeframe: algorithmNode.config.timeframe || "1h",
        parameters: {
          assetClasses: assetClassNodes.map((node) => ({
            type: node.type,
            config: node.config,
          })),
          symbols: symbolNodes.map((node) => ({
            symbol: node.config.symbol,
            assetClass: activeAssetClass || node.config.assetClass,
            maxCapital: node.config.maxCapital,
            takeProfitValue: node.config.takeProfitValue || 0,
            stopLossValue: node.config.stopLossValue || 0,
            maxRiskMode: node.config.maxRiskMode || "percentage",
            maxRiskValue: node.config.maxRiskValue || 0,
            lotSize: node.config.lotSize || 1,
            limitOrderUpdateValue: node.config.limitOrderUpdateValue || 50,
          })),
          algorithm: {
            name: algorithmNode.config.name || "Untitled Algorithm",
            description: algorithmNode.config.description || "",
            timeframe: algorithmNode.config.timeframe || "1h",
            indicators: algorithmNode.config.movingAverages || [],
            entryConditions: {
              buyConditions:
                algorithmNode.config.entryConditions?.buyConditions || [],
              sellConditions:
                algorithmNode.config.entryConditions?.sellConditions || [],
            },
            exitConditions: {
              buyExitConditions:
                algorithmNode.config.exitConditions?.buyExitConditions || [],
              sellExitConditions:
                algorithmNode.config.exitConditions?.sellExitConditions || [],
            },
          },
          risk: riskNode?.config || {
            // General Risk Settings
            lotMode: "Fixed",
            lotValue: 0.1,
            maxOrders: 10,
            allowPyramiding: false,
            allowHedging: false,
            closeOnBiasFlip: true,
            stackEntriesEveryBar: true,
            enterWhenFlat: false,
            maxPyramidPositions: 3,
            pyramidSize: 0.5,

            // Take Profit & Stop Loss Settings
            takeProfitPercentage: 1.0,
            stopLossPercentage: 1.0,
            breakevenAfterProfit: 0,
            closeTradeAfterDrawdown: 0,
            addSpreadToTP: false,
            addSpreadToSL: false,

            // Trailing Stop Settings
            trailingStopEnabled: false,
            trailingStopType: "Percentage",
            trailingStopAmount: 0.5,
            trailingTakeProfitEnabled: false,
            trailingTakeProfitType: "Percentage",
            trailingTakeProfitAmount: 0.5,

            // Order Settings
            orderType: "market",
            limitPriceOffset: 0.1,
            stopPriceOffset: 0.1,
            limitOrderCancelMinutes: 5,
            allowReversal: false,
            limitOrderUpdateInterval: 30, // Default update interval in seconds
            limitOrderUpdateValue: 100, // Default update value in points
            useIntrabarCalculation: false,

            // Drawdown Settings
            maxDrawdown: 1000, // Maximum dollar drawdown
            maxDrawdownPercentage: 10.0, // Maximum percentage drawdown
          },
          schedule: scheduleNode?.config || {
            // Schedule Settings
            activeShutdown: true,
            closeDay: "Friday",
            closeTime: "16:00",
            restartDay: "Monday",
            restartTime: "09:30",
            dailyStartTime: "09:30",
            dailyStopTime: "16:00",
            weeklyShutdown: true,
            marketOpenTime: "09:30",
            marketCloseTime: "16:00",
          },
        },
      };

      // Create or update the algo
      const algoId = await ensureAlgoExists();
      const isEdit = Boolean(id || currentAlgoId);
      const response = await fetch(`/api/algos/${algoId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(algoData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to save algorithm");
      }

      const saved = await response.json();
      setCurrentAlgoId(saved.id);

      setSuccessMessage(
        isEdit
          ? `Algo "${algorithmNode.config.name || "Untitled Algorithm"}" updated successfully`
          : `New algo "${algorithmNode.config.name || "Untitled Algorithm"}" created successfully`
      );

      if (!isEdit) {
        setTimeout(() => {
          resetCanvas();
          setSuccessMessage(null);
        }, 2000);
      } else {
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (error) {
      console.error("Error saving algorithm:", error);
      // TODO: Show error message to user
    } finally {
      setIsSaving(false);
    }
  }, [canvasState.nodes, ensureAlgoExists, resetCanvas, id, currentAlgoId]);

  const handleNodeClick = (node: NodeConfig) => {
    setSelectedNode(node);
  };

  const handleNodeMove = useCallback((nodeId: string, x: number, y: number) => {
    setCanvasState((prev) => ({
      ...prev,
      nodes: prev.nodes.map((node) =>
        node.id === nodeId ? { ...node, position: { x, y } } : node
      ),
    }));
  }, []);

  const handleDelete = useCallback(() => {
    if (selectedNode) {
      setCanvasState((prev) => ({
        ...prev,
        nodes: prev.nodes.filter((node) => node.id !== selectedNode.id),
        connections: prev.connections.filter(
          (conn) =>
            conn.source !== selectedNode.id && conn.target !== selectedNode.id
        ),
      }));
      setSelectedNode(null);
    }
  }, [selectedNode]);

  return (
    <div className="p-4">
      <h4 className="mb-4">Trading Algo Studio</h4>

      {successMessage && (
        <Alert variant="success" className="mb-3">
          {successMessage}
        </Alert>
      )}

      <SaveButton
        variant="primary"
        onClick={handleSaveAlgo}
        disabled={isSaving}
      >
        <Save size={16} className="me-1" />
        {isSaving ? "Saving..." : "Save Algorithm"}
      </SaveButton>

      <StudioContainer>
        <LeftPanel>
          <ComponentGroup>
            <ComponentTitle>Asset Classes</ComponentTitle>
            <DraggableComponent
              type="Stocks"
              name="Stocks"
              disabled={hasAssetClassComponent}
            />
            <DraggableComponent
              type="Stock Options"
              name="Stock Options"
              disabled={hasAssetClassComponent}
            />
            <DraggableComponent
              type="Futures"
              name="Futures"
              disabled={hasAssetClassComponent}
            />
            <DraggableComponent
              type="Futures Options"
              name="Futures Options"
              disabled={hasAssetClassComponent}
            />
            <DraggableComponent
              type="Forex"
              name="Forex"
              disabled={hasAssetClassComponent}
            />
            <DraggableComponent
              type="Crypto Spot"
              name="Crypto Spot"
              disabled={hasAssetClassComponent}
            />
            <DraggableComponent
              type="Crypto Futures"
              name="Crypto Futures"
              disabled={hasAssetClassComponent}
            />
            {hasAssetClassComponent && (
              <ComponentHint>
                Asset classes are limited to one at a time. Delete the existing
                asset class node to select a different one.
              </ComponentHint>
            )}
          </ComponentGroup>

          <ComponentGroup>
            <ComponentTitle>Trading Components</ComponentTitle>
            <DraggableComponent
              type="Symbol"
              name="Symbol"
              disabled={!hasAssetClassComponent}
            />
            <DraggableComponent
              type="Algorithm"
              name="Algorithm"
              disabled={!hasAssetClassComponent}
            />
            <DraggableComponent
              type="Trade Execution and Management"
              name="Trade Execution and Management"
              disabled={!hasAssetClassComponent}
            />
            <DraggableComponent
              type="Schedule"
              name="Schedule"
              disabled={!hasAssetClassComponent}
            />
            {!hasAssetClassComponent && (
              <ComponentHint>
                Select an asset class before adding or configuring other
                components.
              </ComponentHint>
            )}
          </ComponentGroup>
        </LeftPanel>

        <Canvas onDrop={handleDrop}>
          <CanvasControls>
            <Button variant="outline-primary" size="sm">
              <Link size={16} className="me-1" />
              Connect Selected
            </Button>
            <Button variant="outline-primary" size="sm">
              <Unlink size={16} className="me-1" />
              Disconnect Selected
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={handleDelete}
              disabled={!selectedNode}
            >
              <Trash2 size={16} className="me-1" />
              Delete
            </Button>
          </CanvasControls>

          {canvasState.nodes.map((node) => (
            <Node
              key={node.id}
              node={node}
              isSelected={selectedNode?.id === node.id}
              onClick={() => handleNodeClick(node)}
              onMove={handleNodeMove}
            />
          ))}
        </Canvas>

        <RightPanel>
          {selectedNode ? (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Component Configuration</h6>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveComponent}
                  disabled={isSaving}
                >
                  <Save size={16} className="me-1" />
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </div>
              <ConfigurationPanel
                node={selectedNode}
                onConfigChange={handleConfigChange}
                onClose={() => setSelectedNode(null)}
                activeAssetClass={activeAssetClass}
              />
            </>
          ) : (
            <p className="text-muted">Select a component to configure</p>
          )}
        </RightPanel>
      </StudioContainer>
    </div>
  );
};

export default AlgoStudio;
